import { Scene } from '@babylonjs/core/scene';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import type { Vec3 } from '../types';
import { getRuntimeProfile } from '../core/RuntimeProfile';

// Visual diameter exaggerated for visibility (real ball is 4.27cm)
const VISUAL_DIAMETER = 0.12;
const VISUAL_RADIUS = VISUAL_DIAMETER / 2;

// Larger ball for comparison mode — easier to track among 25+ simultaneous shots
const COMPARISON_DIAMETER = 0.5;
const COMPARISON_RADIUS = COMPARISON_DIAMETER / 2;

// Ground shadow disc
const SHADOW_DISC_DIAMETER = 0.8;

export class BallRenderer {
  private mesh: Mesh;
  private shadowDisc: Mesh | null = null;
  private radius: number;

  constructor(scene: Scene, shadowGen: CascadedShadowGenerator, comparison = false) {
    const profile = getRuntimeProfile();
    const diameter = comparison ? COMPARISON_DIAMETER : VISUAL_DIAMETER;
    this.radius = comparison ? COMPARISON_RADIUS : VISUAL_RADIUS;

    this.mesh = MeshBuilder.CreateSphere('ball', {
      diameter,
      segments: comparison ? 12 : profile.ballSegments,
    }, scene);

    const mat = new StandardMaterial('ballMat', scene);
    mat.diffuseColor = new Color3(1, 1, 1);
    mat.specularColor = new Color3(1, 1, 1);
    mat.specularPower = 32;
    mat.emissiveColor = new Color3(0.4, 0.4, 0.4);
    this.mesh.material = mat;

    shadowGen.addShadowCaster(this.mesh);

    // Ground shadow disc for comparison mode — shows ball position projected on ground
    if (comparison) {
      this.shadowDisc = MeshBuilder.CreateDisc('ballShadow', {
        radius: SHADOW_DISC_DIAMETER / 2,
        tessellation: 16,
      }, scene);
      const shadowMat = new StandardMaterial('ballShadowMat', scene);
      shadowMat.diffuseColor = new Color3(0, 0, 0);
      shadowMat.emissiveColor = new Color3(0, 0, 0);
      shadowMat.alpha = 0.25;
      shadowMat.disableLighting = true;
      this.shadowDisc.material = shadowMat;
      this.shadowDisc.rotation.x = Math.PI / 2; // lay flat on ground
      this.shadowDisc.position.y = 0.02; // just above ground
    }

    this.setPosition({ x: 0, y: 0.05, z: 0 });
  }

  setPosition(pos: Vec3): void {
    this.mesh.position.set(pos.x, pos.y + this.radius, pos.z);
    if (this.shadowDisc) {
      this.shadowDisc.position.set(pos.x, 0.02, pos.z);
      // Scale shadow by height — smaller when higher, larger when near ground
      const heightScale = Math.max(0.3, 1 - pos.y / 50);
      this.shadowDisc.scaling.set(heightScale, heightScale, heightScale);
    }
  }

  setInterpolatedPosition(prev: Vec3, next: Vec3, alpha: number): void {
    const x = prev.x + (next.x - prev.x) * alpha;
    const y = prev.y + (next.y - prev.y) * alpha;
    const z = prev.z + (next.z - prev.z) * alpha;
    this.mesh.position.set(x, y + this.radius, z);
    if (this.shadowDisc) {
      this.shadowDisc.position.set(x, 0.02, z);
      const heightScale = Math.max(0.3, 1 - y / 50);
      this.shadowDisc.scaling.set(heightScale, heightScale, heightScale);
    }
  }

  getMesh(): Mesh {
    return this.mesh;
  }

  resetToTee(): void {
    this.setPosition({ x: 0, y: 0.05, z: 0 });
  }

  /** Set the ball color (for comparison shots matching tracer color). */
  setColor(r: number, g: number, b: number): void {
    const mat = this.mesh.material as StandardMaterial;
    mat.diffuseColor = new Color3(r, g, b);
    mat.emissiveColor = new Color3(r * 0.4, g * 0.4, b * 0.4);
    // Tint shadow disc to match ball color
    if (this.shadowDisc) {
      const sMat = this.shadowDisc.material as StandardMaterial;
      sMat.diffuseColor = new Color3(r * 0.3, g * 0.3, b * 0.3);
      sMat.emissiveColor = new Color3(r * 0.15, g * 0.15, b * 0.15);
    }
  }

  /** Dispose the mesh and its material (for cleanup after comparison shots). */
  dispose(): void {
    this.mesh.material?.dispose();
    this.mesh.dispose();
    if (this.shadowDisc) {
      this.shadowDisc.material?.dispose();
      this.shadowDisc.dispose();
      this.shadowDisc = null;
    }
  }
}
